import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Panel } from 'primereact/panel';
import { ProgressSpinner } from 'primereact/progressspinner';
import { InputComponent } from '../../../../components/InputComponent';
import * as generic from '../../../../components/Generico';
import { Patrimonial } from '../../../../models/trust/cliente/Patrimonial';
import { ClientePnService } from '../../../../service/trust/ClientePnService';
import { Divider } from 'primereact/divider';
import { BottomActionBar } from '../../../../components/ui/BottomActionBar';

export const InfoPatrimonial = ({ toast, id, permss, catalogos, handlePrev, handleNext, nuevoCliente, soloLectura }) => {
    const omodel = new Patrimonial();
    const defaultValues = generic.funDefault(omodel.model);
    const { control, formState: { errors }, handleSubmit, reset, setError, watch, setValue } = useForm({ defaultValues, shouldTrim: true });
    const clienteServicio = new ClientePnService();
    const { inNumber, inDropMultiple, inText } = InputComponent({ omodel, errors, control, permss, id, soloLectura });
    // Estados de interfaz y persistencia
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    const corriente = watch('corriente') || 0;
    const fijo = watch('fijo') || 0;
    const otroActivo = watch('otro_activo') || 0;
    const cortoPlazo = watch('corto_plazo') || 0;
    const largoPlazo = watch('largo_plazo') || 0;

    useEffect(() => {
        const totalActivos = corriente + fijo + otroActivo;
        const totalPasivos = cortoPlazo + largoPlazo;
        const patrimonio = totalActivos - totalPasivos;
        // Actualizamos los campos calculados
        setValue('total_activo', totalActivos);
        setValue('total_pasivos', totalPasivos);
        setValue('patrimonio', patrimonio);
    }, [corriente, fijo, otroActivo, cortoPlazo, largoPlazo, setValue]);

    useEffect(() => {
        if (id) {
            editFicha();
        } else {
            setIsLoading(false);
        }
        // eslint-disable-next-line
    }, [id]);

    const onSubmit = (data) => {
        setIsSaving(true);
        data.id = id;
        data.seccion = 'seccion3';

        clienteServicio.editarCliente(data).then(res => {
            nuevoCliente(res.data);
            generic.funcSuccess(res, toast);
            handleNext();
        }).catch(error => {
            generic.funcError(error, toast, setError);
        }).finally(() => {
            setIsSaving(false);
        });
    }

    const editFicha = () => {
        setIsLoading(true);
        clienteServicio.consultarCliente(id, { seccion: 'seccion3' }).then(res => {
            const _cli = res.data.cliente;

            if (!_cli) {
                reset(defaultValues);
                setIsLoading(false);
                return;
            }
            if (_cli.origen_ingreso) {
                _cli.origen_ingreso = catalogos.origen_ingreso.filter(item => _cli.origen_ingreso.includes(item.cat_id));
            }
            reset(generic.renewValues(_cli, omodel.model));
        }).finally(() => {
            setIsLoading(false);
        });
    }

    const onError = () => {
        toast.current.show({ severity: 'error', summary: 'Error', detail: 'Se detectaron errores, por favor revise el formulario para corregir.', life: 3000 });
    }

    if (isLoading) {
        return (
            <div className="flex flex-column align-items-center justify-content-center p-5">
                <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" animationDuration=".5s" />
                <span className="mt-3 text-color-secondary font-medium text-lg">Consultando información...</span>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit(onSubmit, onError)} className="p-fluid">

            <Panel header={generic.renderPanelHeader('E', 'INFORMACIÓN PATRIMONIAL A LA FECHA', 'pi pi-chart-line')} className="panel-ficha shadow-1 mb-4">
                <div className="grid formgrid">
                    <div className="col-12 md:col-6 mb-3 md:mb-0">
                        <div className="p-3 surface-50 border-round h-full flex flex-column shadow-1">
                            <div>
                                <div className="text-center text-primary font-bold mb-3 border-bottom-1 pb-2 text-lg">ACTIVOS</div>
                                {inNumber('corriente', 2, true, { icon: 'pi pi-money-bill', iconPosition: 'left' }, 12)}
                                {inNumber('fijo', 2, true, { icon: 'pi pi-building', iconPosition: 'left' }, 12)}
                                {inNumber('otro_activo', 2, true, { icon: 'pi pi-box', iconPosition: 'left' }, 12)}
                            </div>

                            <div className="mt-auto">
                                <Divider type="dashed" className="my-2" />
                                <div className="bg-blue-50 p-2 border-round border-1 border-blue-200">
                                    {inNumber('total_activo', 2, true, { icon: 'pi pi-chart-bar', iconPosition: 'left', disabled: true }, 12)}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-12 md:col-6">
                        <div className="p-3 surface-50 border-round h-full flex flex-column shadow-1">
                            <div>
                                <div className="text-center text-primary font-bold mb-3 border-bottom-1 pb-2 text-lg">PASIVOS</div>
                                {inNumber('corto_plazo', 2, true, { icon: 'pi pi-calendar-minus', iconPosition: 'left' }, 12)}
                                {inNumber('largo_plazo', 2, true, { icon: 'pi pi-calendar-plus', iconPosition: 'left' }, 12)}
                            </div>

                            <div className="mt-auto">
                                <Divider type="dashed" className="my-2" />
                                <div className="bg-blue-50 p-2 border-round border-1 border-blue-200">
                                    {inNumber('total_pasivos', 2, true, { icon: 'pi pi-chart-bar', iconPosition: 'left', disabled: true }, 12)}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid mt-3">
                    <div className="col-12 md:col-6 md:col-offset-3">
                        <div className="bg-blue-100 p-3 border-round border-1 border-blue-300 shadow-2 text-center">
                            {inNumber('patrimonio', 2, true, { icon: 'pi pi-wallet', iconPosition: 'left', disabled: true }, 12)}
                        </div>
                    </div>
                </div>
            </Panel>

            <Panel header={generic.renderPanelHeader('F', 'INGRESOS, EGRESOS Y ORIGEN DE FONDOS', 'pi pi-wallet')} className="panel-ficha shadow-1 mb-4">
                <div className="formgrid grid">
                    {inNumber('ingreso_estimado_mensual', 2, true, { icon: 'pi pi-arrow-up-right', iconPosition: 'left' }, 6)}
                    {inNumber('egreso_estimado_mensual', 2, true, { icon: 'pi pi-arrow-down-right', iconPosition: 'left' }, 6)}
                    {inDropMultiple('origen_ingreso', catalogos?.origen_ingreso, { lab: 'nombre' }, 6)}
                    {inText('otro_ingreso_detalle', { icon: 'pi pi-pencil' }, 6)}
                </div>
            </Panel>

            <BottomActionBar layoutVersion="v2" onPrev={handlePrev} onNext={handleNext} loading={isSaving} isReadOnly={soloLectura} />
        </form>
    );

}